import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { ReactComponent as CalendarIcon } from 'assets/images/svg/calender-search-icon.svg';
import { ReactComponent as ChevronRight } from 'assets/images/svg/chevron-right.svg';

import moment from 'moment';
import CustomHeader from 'components/shared/searchDatePicker/CustomHeader';
import siteStyle from 'utils/siteStyle';

const inputId = `date-picker-${uniqueId()}`;

const SearchDatePicker = ({
  wrapperClassName,
  monthsShown,
  value,
  dateFormat,
  minDate,
  maxDate,
  onSelected,
}) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const onChangeDate = useCallback((date) => {
    onSelected(date);
  }, [onSelected]);

  const renderDateNavigator = (
    <div className="flex items-center gap-8">
      <button
        type="button"
        className="border-none focus:outline-none"
        onClick={(e) => {
          e.stopPropagation();
          if (value <= minDate) return;
          onChangeDate(moment(value).add(-1, 'day').toDate());
        }}
      >
        <ChevronRight className={classnames('rotate-180 h-4 w-4 fill-current text-dark', {
          'text-light-grey-blue-two': value <= minDate,
        })}
        />
      </button>
      <button
        type="button"
        className="border-none focus:outline-none"
        onClick={(e) => {
          e.stopPropagation();
          onChangeDate(moment(value).add(1, 'day').toDate());
        }}
      >
        <ChevronRight className="w-4 h-4 fill-current text-dark" />
      </button>
    </div>
  );

  return (
    <label
      htmlFor={inputId}
      className={classnames(
        wrapperClassName,
        { 'md:border-primary': isCalendarOpen },
        siteStyle({
          common: 'flex justify-between h-14 bg-white border px-4 border-light-grey-3 cursor-pointer',
          default: 'rounded-3',
          foh: 'rounded-2',
          gd: 'rounded-none',
        }),
      )}
    >
      <div className="search-date-picker w-full flex items-center gap-4">
        <div>
          <CalendarIcon className="w-5 h-5 fill-current text-dark" />
        </div>
        <div className="flex flex-col justify-center flex-grow">
          <label
            htmlFor={inputId}
            className={siteStyle({
              common: 'text-3.5 leading-4 -tracking-0.2 text-grey-3 cursor-pointer flex-1',
              // bt: 'font-korolav uppercase',
              foh: 'font-roboto',
              default: 'font-primary-two',
            })}
          >
            Date
          </label>
          <DatePicker
            id={inputId}
            wrapperClassName="leading-3 min-w-16"
            className={siteStyle({ common: 'w-full text-4 leading-5 text-dark -tracking-0.2 cursor-pointer focus:outline-none', default: 'font-primary font-medium', foh: 'font-roboto' })}
            useWeekdaysShort
            renderCustomHeader={(props) => (
              <CustomHeader
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
              />
            )}
            showPopperArrow={false}
            dayClassName={() => 'p-2 text-2.5 font-primary -tracking-0.3'}
            selected={value}
            onChange={onChangeDate}
            minDate={minDate}
            maxDate={maxDate}
            dateFormat={dateFormat}
            monthsShown={monthsShown}
            onClickOutside={() => setIsCalendarOpen(false)}
            popperClassName="hidden md:block"
            placeholderText="Enter date"
            shouldCloseOnSelect
            onCalendarOpen={() => setIsCalendarOpen(true)}
            onCalendarClose={() => setIsCalendarOpen(false)}
            popperPlacement="bottom-start"
            popperModifiers={{
              offset: {
                enabled: true,
                offset: '-52px, 16px',
              },
              flip: {
                enabled: false,
              },
              preventOverflow: {
                enabled: true,
                escapeWithReference: true,
              },
              hide: {
                enabled: false,
              },
            }}
            onChangeRaw={(e) => e.preventDefault()}
          />
        </div>
      </div>
      {renderDateNavigator}
    </label>
  );
};

SearchDatePicker.defaultProps = {
  monthsShown: 1,
  value: new Date(),
  minDate: new Date(),
  maxDate: null,
  wrapperClassName: '',
  dateFormat: 'MMM d',
  onSelected: null,
};

SearchDatePicker.propTypes = {
  monthsShown: PropTypes.number,
  value: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  wrapperClassName: PropTypes.string,
  dateFormat: PropTypes.string,
  onSelected: PropTypes.func,
};

export default SearchDatePicker;
